/**
 * The hook side of `canvasItemLightingProps` and `normalMappedLightingProps`. It owns one set of
 * uniform objects per item for its whole life and mutates their `.value`: three captures what
 * `onBeforeCompile` assigns at first compile, and R3F never sets `needsUpdate`, so a fresh uniform
 * object never reaches the GPU.
 */
import { useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { useCanvasModulate } from '../canvasModulate.js';
import { useCanvasLighting2D, useCapItemLights, useRegisterLitItem } from './CanvasLighting2D.js';
import { canvasItemLightingProps, } from './canvasItemLighting.js';
import { placementId } from './itemLightList.js';
import { releasedOnUnmount } from '../sharedTexture.js';
import { useCanvasLayerIndex, useEffectiveZ } from './canvasItemPlacement.js';
import { CanvasItemMaterialLightMode } from '../../godot/scene/canvasItemMaterial.js';
import { normalMappedLightingProps, } from './normalMappedLighting.js';
import { defaultWhiteTexture } from '../materials/defaultWhiteTexture.js';
/** `texture`, read raw and uploaded on first use. */
function rawTexel(texture) {
    texture.colorSpace = THREE.NoColorSpace;
    texture.needsUpdate = true;
    return texture;
}
/**
 * Bound while no light reaches the item, and as the tint of a list no light tints. Transparent
 * black, so the tint adds nothing, and a sampler uniform still has to point at a real texture.
 */
const EMPTY_LIGHT_BUFFER = releasedOnUnmount(rawTexel(new THREE.DataTexture(new Uint8Array([0, 0, 0, 0]), 1, 1)));
/** The `shadow_color` layers of a list no light tints: one transparent black layer. */
const EMPTY_LIGHT_LAYERS = releasedOnUnmount(rawTexel(new THREE.DataArrayTexture(new Uint8Array([0, 0, 0, 0]), 1, 1, 1)));
/** A flat normal, (0, 0, 1) encoded, until the item binds its own map. */
const FLAT_NORMAL_MAP = releasedOnUnmount(rawTexel(new THREE.DataTexture(new Uint8Array([128, 128, 255, 255]), 1, 1)));
function createUniforms(resolution) {
    return {
        lightBuffer: { value: EMPTY_LIGHT_BUFFER },
        shadowTintBuffer: { value: EMPTY_LIGHT_BUFFER },
        isLit: { value: 0 },
        resolution: { value: resolution },
        canvasModulate: { value: new THREE.Vector3(1, 1, 1) },
        lightMode: { value: CanvasItemMaterialLightMode.NORMAL },
    };
}
/** The normal-mapped program's uniforms, sharing the resolution, tint and mode objects of `plain`. */
function createNormalMappedUniforms(plain) {
    return {
        resolution: plain.resolution,
        canvasModulate: plain.canvasModulate,
        lightMode: plain.lightMode,
        lightLayers: { value: EMPTY_LIGHT_LAYERS },
        shadowTintLayers: { value: EMPTY_LIGHT_LAYERS },
        places: { value: [] },
        modes: { value: [] },
        normalMap: { value: FLAT_NORMAL_MAP },
        specularMap: { value: defaultWhiteTexture() },
        specular: { value: new THREE.Vector4(1, 1, 1, 1) },
        usesSpecular: { value: 0 },
    };
}
/** Points `uniforms` at the lights `apart` keeps and at the maps of `surface`. */
function bindNormalMapped(uniforms, apart, surface) {
    uniforms.lightLayers.value = apart.buffer;
    uniforms.shadowTintLayers.value = apart.shadowTintBuffer ?? EMPTY_LIGHT_LAYERS;
    uniforms.places.value = apart.places;
    uniforms.modes.value = apart.modes;
    uniforms.normalMap.value = surface.normalMap;
    // Godot's default specular map is white (`texture_storage.cpp:773-776`).
    uniforms.specularMap.value = surface.specularMap ?? defaultWhiteTexture();
    const { specular } = surface;
    if (specular)
        uniforms.specular.value.set(specular.r, specular.g, specular.b, specular.a);
    uniforms.usesSpecular.value = specular ? 1 : 0;
}
/**
 * The normal-mapped program's uniforms, created once and bound to `apart` and `surface` while both
 * are there.
 */
function useNormalMappedUniforms(plain, apart, surface) {
    const uniforms = useRef(null);
    uniforms.current ??= createNormalMappedUniforms(plain);
    if (apart && surface)
        bindNormalMapped(uniforms.current, apart, surface);
    return uniforms.current;
}
export function useCanvasItemLighting({ material, lightMask = 1, effectiveZ, surface = null, }) {
    const { lists, resolution } = useCanvasLighting2D();
    const inheritedZ = useEffectiveZ();
    const itemZ = effectiveZ ?? inheritedZ;
    const canvasLayer = useCanvasLayerIndex();
    // The raw canvas tint, not the light-mode-gated one: the shader divides out
    // exactly what the CPU folded in, and the floor is applied on both sides.
    const canvasModulate = useCanvasModulate();
    const lightMode = material?.lightMode ?? CanvasItemMaterialLightMode.NORMAL;
    const lightOnly = lightMode === CanvasItemMaterialLightMode.LIGHT_ONLY;
    // Godot's cull test (`light_mask`, `z_final` and the canvas layer, at
    // `renderer_canvas_render_rd.cpp:2366` and `renderer_viewport.cpp:672`) picks the item's light
    // list, so items at one placement share it. The unmodulated accumulation costs a second pre-pass,
    // so a list has one only while a Light Only item reads it. Past 15 positional lights on its rect, or
    // past the render's budget, the item drops the later ones from its list.
    const geometryRef = useRef(null);
    const [takenLights, setTakenLights] = useState(null);
    const placement = { lightMask, z: itemZ, layer: canvasLayer, takenLights };
    useCapItemLights(placement, geometryRef, setTakenLights);
    const readsNormals = surface !== null && lightMode !== CanvasItemMaterialLightMode.UNSHADED;
    const read = readsNormals ? 'apart' : lightOnly ? 'lightOnly' : 'plain';
    useRegisterLitItem(placement, read);
    const list = lists.get(placementId(placement));
    const uniforms = useRef(null);
    uniforms.current ??= createUniforms(resolution);
    const bound = uniforms.current;
    // Mutating in render keeps the GPU in step without a recompile, and re-running
    // plain value writes is harmless.
    const accumulation = lightOnly ? list?.lightOnlyBuffer : list?.buffer;
    bound.isLit.value = accumulation ? 1 : 0;
    bound.lightBuffer.value = accumulation ?? EMPTY_LIGHT_BUFFER;
    bound.shadowTintBuffer.value = (accumulation ? list?.shadowTintBuffer : null) ?? EMPTY_LIGHT_BUFFER;
    // The provider owns these textures and this vector, so a resize or a
    // reallocation reaches every item without a re-render.
    bound.resolution.value = resolution;
    bound.canvasModulate.value.set(canvasModulate.r, canvasModulate.g, canvasModulate.b);
    // The mode rides the uniforms too: a re-parse changes `light_mode` under a
    // mounted item, and three would keep the program it first compiled.
    bound.lightMode.value = lightMode;
    // A normal map scales each light by its own normal term, so it shades the lights the list keeps
    // apart.
    const apart = read === 'apart' ? (list?.apart ?? null) : null;
    const shaded = useNormalMappedUniforms(bound, apart, surface);
    const apartCount = apart?.places.length ?? 0;
    // Mode-independent by design: the props change only with the count of lights kept apart, which
    // sizes the program, and the key that follows it remounts the material.
    const props = useMemo(() => (apartCount > 0 ? normalMappedLightingProps(shaded, apartCount) : canvasItemLightingProps(bound)), [bound, shaded, apartCount]);
    return useMemo(() => ({ props, geometryRef }), [props]);
}
//# sourceMappingURL=useCanvasItemLighting.js.map